import React, { useState, useEffect } from 'react';

const FONT = "'Inter', system-ui, sans-serif";

const Icon = ({ d, size = 18, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const STATUS_CONFIG = {
    Scheduled: {
        color: '#3b82f6',
        light: '#eff6ff',
        border: '#bfdbfe',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        gradient: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
        badgeBg: '#dbeafe',
        badgeColor: '#1d4ed8',
    },
    Ongoing: {
        color: '#f59e0b',
        light: '#fffbeb',
        border: '#fde68a',
        icon: 'M13 10V3L4 14h7v7l9-11h-7z',
        gradient: 'linear-gradient(135deg, #d97706, #f59e0b)',
        badgeBg: '#fef3c7',
        badgeColor: '#b45309',
    },
    Completed: {
        color: '#10b981',
        light: '#ecfdf5',
        border: '#a7f3d0',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        gradient: 'linear-gradient(135deg, #059669, #10b981)',
        badgeBg: '#d1fae5',
        badgeColor: '#065f46',
    },
};

function TrackingModalPlaceholder() { return null; } // Removed as it is now a separate page

export default function OperatorTripList({ user, status, onBack, onNavigate }) {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [updatingId, setUpdatingId] = useState(null);
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Scheduled;

    useEffect(() => {
        if (!operatorId) return;
        setLoading(true);
        fetch(`/api/trips/${operatorId}`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setTrips(data.filter(t => (t.status || 'Scheduled').toLowerCase() === status.toLowerCase()));
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [operatorId, status]);

    const updateStatus = async (tripId, newStatus) => {
        setUpdatingId(tripId);
        try {
            await fetch(`/api/trips/${tripId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            setTrips(prev => prev.filter(t => t._id !== tripId));
        } catch (err) {
            console.error(err);
        } finally {
            setUpdatingId(null);
        }
    };

    const downloadTripCSV = (trip) => {
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const headers = [
            'Trip ID', 'Trip Type', 'Status', 'Route Name',
            'Pickup Location', 'Drop Location',
            'Driver Name', 'Driver Phone',
            'Vehicle Reg', 'Vehicle Make', 'Vehicle Model',
            'Client', 'Billing Type', 'Amount',
            'Pickup Date', 'Drop Date', 'Created At'
        ];
        const row = [
            trip._id,
            trip.tripType || 'N/A',
            trip.status || 'Completed',
            trip.routeId?.name || 'Custom Route',
            trip.routeId?.pickupLocation || trip.pickupLocation || 'N/A',
            trip.routeId?.dropLocation || trip.dropLocation || 'N/A',
            trip.driverId?.name || 'Unassigned',
            trip.driverId?.phoneNumber || 'N/A',
            trip.vehicleId?.registrationNumber || 'Unassigned',
            trip.vehicleId?.make || 'N/A',
            trip.vehicleId?.model || 'N/A',
            trip.clientId?.companyName || trip.clientId?.name || 'N/A',
            trip.billingType || 'N/A',
            trip.amount ?? trip.proposedPrice ?? 'N/A',
            trip.pickupDate ? new Date(trip.pickupDate).toLocaleString() : 'N/A',
            trip.dropDate ? new Date(trip.dropDate).toLocaleString() : 'N/A',
            trip.createdAt ? new Date(trip.createdAt).toLocaleString() : 'N/A',
        ];
        const csvContent = [
            headers.map(esc).join(','),
            row.map(esc).join(',')
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trip-sheet-${trip._id?.slice(-8) || 'export'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const filtered = trips.filter(t => {
        const q = search.toLowerCase();
        return (
            (t.routeId?.name || 'Custom Route').toLowerCase().includes(q) ||
            (t.driverId?.name || '').toLowerCase().includes(q) ||
            (t.vehicleId?.registrationNumber || '').toLowerCase().includes(q) ||
            (t.tripType || '').toLowerCase().includes(q)
        );
    });

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: FONT }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                .trip-row { transition: background 0.15s ease; }
                .trip-row:hover { background: #f8fafc !important; }
                .action-btn { transition: all 0.15s ease; }
                .action-btn:hover { transform: translateY(-1px); filter: brightness(1.1); }
                .back-btn:hover { background: #e2e8f0 !important; }
                .status-select:focus { outline: none; box-shadow: 0 0 0 3px ${cfg.color}30; }
            `}</style>

            {/* Header Banner */}
            <div style={{ background: cfg.gradient, padding: '28px 40px 32px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ position: 'absolute', bottom: -60, right: 100, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                <div style={{ position: 'relative', zIndex: 2 }}>
                    <button className="back-btn" onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 20, backdropFilter: 'blur(8px)', transition: 'background 0.15s' }}>
                        <Icon d="M19 12H5M12 19l-7-7 7-7" size={16} color="white" /> Back to Dashboard
                    </button>

                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                                <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.2)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
                                    <Icon d={cfg.icon} size={22} color="white" />
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Trip Management</div>
                                    <h1 style={{ fontSize: 26, fontWeight: 900, color: 'white', margin: 0, letterSpacing: '-0.02em' }}>{status} Trips</h1>
                                </div>
                            </div>
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 14, padding: '14px 24px', textAlign: 'center' }}>
                            <div style={{ fontSize: 36, fontWeight: 900, color: 'white', lineHeight: 1 }}>{trips.length}</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>Total Trips</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div style={{ padding: '24px 40px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'white', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: '0 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)', maxWidth: 420 }}>
                    <Icon d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={16} color="#94a3b8" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={`Search ${status.toLowerCase()} trips...`}
                        style={{ flex: 1, border: 'none', outline: 'none', padding: '12px 0', fontSize: 13.5, fontFamily: FONT, color: '#334155', background: 'transparent' }}
                    />
                    {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>×</button>}
                </div>
            </div>

            {/* Trip Table */}
            <div style={{ padding: '20px 40px 40px', animation: 'fadeUp 0.35s ease' }}>
                <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

                    {loading ? (
                        <div style={{ padding: 60, textAlign: 'center' }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ height: 64, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', borderRadius: 10, marginBottom: 12 }} />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <div style={{ width: 64, height: 64, borderRadius: 20, background: cfg.light, border: `2px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <Icon d={cfg.icon} size={28} color={cfg.color} />
                            </div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>No {status} Trips Found</div>
                            <div style={{ fontSize: 13.5, color: '#94a3b8' }}>{search ? 'Try a different search term' : `No trips currently have ${status} status`}</div>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                                        {['Trip Type', 'Route', 'Driver', 'Fleet / Vehicle', 'Change Status', 'Actions'].map(h => (
                                            <th key={h} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: h === 'Actions' ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((trip, idx) => (
                                        <tr key={trip._id} className="trip-row" style={{ borderBottom: '1px solid #f1f5f9', background: updatingId === trip._id ? '#fafbff' : 'white', animationDelay: `${idx * 0.04}s` }}>
                                            <td style={{ padding: '18px 18px' }}>
                                                <span style={{ background: cfg.badgeBg, color: cfg.badgeColor, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                    {trip.tripType || 'Scheduled'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '18px 18px', maxWidth: 240 }}>
                                                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{trip.routeId?.name || 'Custom Route'}</div>
                                                <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <span style={{ color: '#475569' }}>{trip.routeId?.pickupLocation || trip.pickupLocation || '—'}</span>
                                                    <Icon d="M5 12h14M12 5l7 7-7 7" size={12} color="#94a3b8" />
                                                    <span style={{ color: '#475569' }}>{trip.routeId?.dropLocation || trip.dropLocation || '—'}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '18px 18px' }}>
                                                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#334155' }}>{trip.driverId?.name || 'Unassigned'}</div>
                                                <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>{trip.driverId?.phoneNumber || '—'}</div>
                                            </td>
                                            <td style={{ padding: '18px 18px' }}>
                                                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#334155' }}>{trip.vehicleId?.registrationNumber || 'Unassigned'}</div>
                                                <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>{trip.vehicleId?.make ? `${trip.vehicleId.make} ${trip.vehicleId.model || ''}`.trim() : '—'}</div>
                                            </td>
                                            <td style={{ padding: '18px 18px' }}>
                                                <select
                                                    className="status-select"
                                                    value={trip.status || 'Scheduled'}
                                                    onChange={e => updateStatus(trip._id, e.target.value)}
                                                    disabled={updatingId === trip._id}
                                                    style={{ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 13, background: cfg.light, color: cfg.badgeColor, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, transition: 'all 0.15s' }}
                                                >
                                                    <option value="Scheduled">Scheduled</option>
                                                    <option value="Ongoing">Ongoing</option>
                                                    <option value="Completed">Completed</option>
                                                </select>
                                            </td>
                                            <td style={{ padding: '18px 18px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    {status === 'Ongoing' && (
                                                        <button className="action-btn" onClick={() => onNavigate('trip-tracking', trip._id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', color: 'white', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                                                            <Icon d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" size={14} color="white" /> Track
                                                        </button>
                                                    )}
                                                    {status === 'Completed' && (
                                                        <button className="action-btn" onClick={() => downloadTripCSV(trip)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'linear-gradient(135deg,#059669,#10b981)', color: 'white', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                                                            <Icon d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-3 3m0 0l-3-3m3 3V4" size={14} color="white" /> Trip Sheet
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: 12, fontSize: 12.5, color: '#94a3b8', textAlign: 'right', fontWeight: 500 }}>
                    {filtered.length} trip{filtered.length !== 1 ? 's' : ''} shown
                    {search && ` · filtered from ${trips.length} total`}
                </div>
            </div>

            {/* Removed TrackingModal call as it is now a separate page */}
        </div>
    );
}
